import { useState, useMemo } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { financeTransactions } from '@/mocks/cash';
import { appointments } from '@/mocks/appointments';
import { customers } from '@/mocks/customers';
import { expenses, vendorBalances } from '@/mocks/expenses';
import { staffMembersData, staffCommissions } from '@/mocks/staff';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';

/* ── Helpers ── */
function parseDate(d: string) { return new Date(d + 'T00:00:00').getTime(); }

function fmtMoney(n: number) {
  return '₺' + Math.round(n).toLocaleString('tr-TR');
}

function fmtDate(d: string) {
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}`;
}

/* ── Page ── */
export default function RaporlarPage() {
  const { theme } = useTheme();

  // Date range
  const [dateFrom, setDateFrom] = useState('2026-05-01');
  const [dateTo, setDateTo] = useState('2026-05-31');
  const [bankModalOpen, setBankModalOpen] = useState(false);

  const chartTextColor = theme === 'dark' ? '#8A8680' : '#6B6560';
  const chartGridColor = theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  // Filter helpers
  const inRange = (date: string) => {
    const t = parseDate(date);
    return t >= parseDate(dateFrom) && t <= parseDate(dateTo);
  };

  /* ── DATA ── */
  // Cash (sales)
  const filteredCash = useMemo(() => financeTransactions.filter(t => t.type === 'income' && inRange(t.date)), [dateFrom, dateTo]);
  const totalSales = useMemo(() => filteredCash.reduce((s, t) => s + t.amount, 0), [filteredCash]);
  const productSales = 0; // no product sales in current data
  const serviceSales = totalSales;

  // Payment distribution
  const cashAmount = useMemo(() => filteredCash.filter(t => t.paymentType === 'Nakit').reduce((s, t) => s + t.amount, 0), [filteredCash]);
  const cardAmount = useMemo(() => filteredCash.filter(t => t.paymentType === 'Kredi Kartı').reduce((s, t) => s + t.amount, 0), [filteredCash]);
  const transferAmount = useMemo(() => filteredCash.filter(t => t.paymentType === 'Havale').reduce((s, t) => s + t.amount, 0), [filteredCash]);
  const openAmount = useMemo(() => filteredCash.filter(t => t.isOpenPayment).reduce((s, t) => s + t.amount, 0), [filteredCash]);
  const bankTotal = cardAmount + transferAmount;

  // Bank detail
  const bankBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    filteredCash.forEach(t => {
      if (t.bankName) {
        map[t.bankName] = (map[t.bankName] || 0) + t.amount;
      }
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filteredCash]);

  // Sales detail (by type from cash transactions)
  const serviceAdisyon = totalSales;
  const packageSales = 0; // no separate package sales tracking yet
  const productAdisyon = 0;

  // Expenses
  const filteredExpenses = useMemo(() => expenses.filter(e => inRange(e.date)), [dateFrom, dateTo]);
  const totalExpenses = useMemo(() => filteredExpenses.reduce((s, e) => s + e.amount, 0), [filteredExpenses]);
  const expenseByCategory = useMemo(() => {
    const map: Record<string, number> = {};
    filteredExpenses.forEach(e => { map[e.category] = (map[e.category] || 0) + e.amount; });
    return map;
  }, [filteredExpenses]);
  const masraf = expenseByCategory['Malzeme'] || 0 + expenseByCategory['Reklam'] || 0 + expenseByCategory['Temizlik / Bakım'] || 0 + expenseByCategory['Diğer'] || 0 + expenseByCategory['Fatura'] || 0;
  const personelGider = expenseByCategory['Personel'] || 0;
  const kiraGider = expenseByCategory['Kira'] || 0;

  // Appointments (customers)
  const filteredAppointments = useMemo(() => appointments.filter(a => inRange(a.date)), [dateFrom, dateTo]);
  const gelenMusteri = useMemo(() => filteredAppointments.filter(a => a.status === 'Geldi').length, [filteredAppointments]);
  const kaydedilenMusteri = useMemo(() => {
    const names = new Set(filteredAppointments.map(a => a.customerName));
    return names.size;
  }, [filteredAppointments]);
  const adisyonSayisi = filteredAppointments.length;
  const adisyonOrtalama = adisyonSayisi > 0 ? totalSales / adisyonSayisi : 0;
  const cagriSayisi = useMemo(() => filteredAppointments.filter(a => a.source === 'Çağrı').length, [filteredAppointments]);

  // Staff summary
  const staffSummary = useMemo(() => {
    return staffMembersData.filter(s => s.role === 'Personel').map(s => {
      const personelPrim = staffCommissions.filter(c => c.staffId === s.id).reduce((sum, c) => sum + c.commissionAmount, 0);
      const personelHizmet = filteredAppointments.filter(a => a.staff.includes(s.fullName.split(' ')[0]) && a.status === 'Geldi').length;
      const maas = s.salary;
      const avans = s.advance;
      const bakiye = maas + personelPrim - avans;
      return {
        id: s.id,
        name: s.fullName,
        hizmet: personelHizmet,
        prim: personelPrim,
        maas,
        avans,
        bakiye,
        compensationType: s.compensationType,
      };
    });
  }, [filteredAppointments]);

  // Vendor balances
  const vendorData = vendorBalances;

  // Chart data
  const salesVsExpenseData = [
    { name: 'Satışlar', value: totalSales },
    { name: 'Giderler', value: totalExpenses },
    { name: 'Net', value: totalSales - totalExpenses },
  ];

  const collectionPieData = [
    { name: 'Nakit', value: cashAmount },
    { name: 'Kredi Kartı', value: cardAmount },
    { name: 'Havale', value: transferAmount },
    { name: 'Açık', value: openAmount },
  ].filter(d => d.value > 0);

  const expensePieData = [
    { name: 'Kira', value: kiraGider },
    { name: 'Personel', value: personelGider },
    { name: 'Diğer', value: masraf },
  ].filter(d => d.value > 0);

  const pieColors = ['#C9A962', '#2D5F3E', '#3B82F6', '#EF4444', '#8B5CF6', '#F59E0B'];

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header + Date Range */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>Raporlar</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            Dönem: {fmtDate(dateFrom)} — {fmtDate(dateTo)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" className="spa-input text-sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>-</span>
          <input type="date" className="spa-input text-sm" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          <button onClick={() => window.print()} className="spa-btn-secondary text-sm flex items-center gap-2 whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line text-sm" /></div>
            Yazdır
          </button>
        </div>
      </div>

      {/* ── BIG SUMMARY CARDS ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Toplam Satış', value: fmtMoney(totalSales), icon: 'ri-coins-line', color: '#C9A962', sub: `${adisyonSayisi} adisyon` },
          { label: 'Toplam Tahsilat', value: fmtMoney(totalSales - openAmount), icon: 'ri-wallet-3-line', color: '#10B981', sub: `Açık: ${fmtMoney(openAmount)}` },
          { label: 'Toplam Gider', value: fmtMoney(totalExpenses), icon: 'ri-money-cny-circle-line', color: '#EF4444', sub: `${filteredExpenses.length} kayıt` },
          { label: 'Net Kalan', value: fmtMoney(totalSales - totalExpenses), icon: 'ri-safe-2-line', color: '#2D5F3E', sub: 'Kâr / Zarar' },
        ].map(c => (
          <div key={c.label} className="spa-card p-4 flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-spa flex items-center justify-center" style={{ background: c.color + '12' }}>
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${c.icon} text-sm`} style={{ color: c.color }} /></div>
              </div>
              <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{c.label}</span>
            </div>
            <p className="text-xl md:text-2xl font-bold font-display" style={{ color: c.color }}>{c.value}</p>
            <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>{c.sub}</p>
          </div>
        ))}
      </div>

      {/* ── CHARTS ROW ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Sales vs Expenses */}
        <div className="spa-card p-4 md:p-5">
          <h3 className="text-sm font-semibold font-display mb-3" style={{ color: 'var(--text-primary)' }}>Satış vs Gider</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={salesVsExpenseData} barSize={36}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                <XAxis dataKey="name" tick={{ fill: chartTextColor, fontSize: 11 }} axisLine={{ stroke: chartGridColor }} />
                <YAxis tick={{ fill: chartTextColor, fontSize: 11 }} axisLine={{ stroke: chartGridColor }} tickFormatter={(v) => `₺${(v/1000)}K`} />
                <Tooltip contentStyle={{ background: theme === 'dark' ? '#1A1A20' : '#FFFFFF', border: `1px solid ${chartGridColor}`, borderRadius: '8px', color: theme === 'dark' ? '#E8E4DC' : '#1A1A1A' }} formatter={(value: number) => [fmtMoney(value), '']} />
                <Bar dataKey="value" radius={[4,4,0,0]}>
                  {salesVsExpenseData.map((_, i) => (
                    <Cell key={i} fill={pieColors[i % pieColors.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Collection Distribution */}
        <div className="spa-card p-4 md:p-5">
          <h3 className="text-sm font-semibold font-display mb-3" style={{ color: 'var(--text-primary)' }}>Tahsilat Dağılımı</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={collectionPieData} cx="50%" cy="50%" innerRadius={30} outerRadius={60} paddingAngle={3} dataKey="value" stroke="none">
                  {collectionPieData.map((_, i) => <Cell key={i} fill={pieColors[i % pieColors.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: theme === 'dark' ? '#1A1A20' : '#FFFFFF', border: `1px solid ${chartGridColor}`, borderRadius: '8px', color: theme === 'dark' ? '#E8E4DC' : '#1A1A1A' }} formatter={(value: number, name: string) => [fmtMoney(value), name]} />
                <Legend iconType="circle" iconSize={6} wrapperStyle={{ fontSize: 10, color: chartTextColor }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Expense Distribution */}
        <div className="spa-card p-4 md:p-5">
          <h3 className="text-sm font-semibold font-display mb-3" style={{ color: 'var(--text-primary)' }}>Gider Dağılımı</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={expensePieData} cx="50%" cy="50%" innerRadius={30} outerRadius={60} paddingAngle={3} dataKey="value" stroke="none">
                  {expensePieData.map((_, i) => <Cell key={i} fill={[pieColors[1], pieColors[4], pieColors[5]][i % 3]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: theme === 'dark' ? '#1A1A20' : '#FFFFFF', border: `1px solid ${chartGridColor}`, borderRadius: '8px', color: theme === 'dark' ? '#E8E4DC' : '#1A1A1A' }} formatter={(value: number, name: string) => [fmtMoney(value), name]} />
                <Legend iconType="circle" iconSize={6} wrapperStyle={{ fontSize: 10, color: chartTextColor }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ── DETAIL TABLES ROW ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Sales Detail */}
        <div className="spa-card p-4">
          <h3 className="text-sm font-semibold font-display mb-3" style={{ color: 'var(--text-primary)' }}>Satış Dağılımı</h3>
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                <td className="py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>Hizmet Satışı</td>
                <td className="py-2 text-sm font-semibold text-right" style={{ color: 'var(--text-primary)' }}>{fmtMoney(serviceSales)}</td>
              </tr>
              <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                <td className="py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>Ürün Satışı</td>
                <td className="py-2 text-sm font-semibold text-right" style={{ color: 'var(--text-primary)' }}>{fmtMoney(productSales)}</td>
              </tr>
              <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                <td className="py-2 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Toplam Satış</td>
                <td className="py-2 text-sm font-bold text-right" style={{ color: '#C9A962' }}>{fmtMoney(totalSales)}</td>
              </tr>
              <tr><td className="py-1" /></tr>
              <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                <td className="py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>Hizmet Adisyon</td>
                <td className="py-2 text-sm font-semibold text-right" style={{ color: 'var(--text-primary)' }}>{fmtMoney(serviceAdisyon)}</td>
              </tr>
              <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                <td className="py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>Paket Satışı</td>
                <td className="py-2 text-sm font-semibold text-right" style={{ color: 'var(--text-primary)' }}>{fmtMoney(packageSales)}</td>
              </tr>
              <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                <td className="py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>Ürün Adisyon</td>
                <td className="py-2 text-sm font-semibold text-right" style={{ color: 'var(--text-primary)' }}>{fmtMoney(productAdisyon)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Collection Detail */}
        <div className="spa-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Tahsilat Dağılımı</h3>
            <button onClick={() => setBankModalOpen(true)} className="text-xs flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer" style={{ background: 'rgba(59,130,246,0.12)', color: '#3B82F6' }}>
              <div className="w-3 h-3 flex items-center justify-center"><i className="ri-bank-line text-xs" /></div>
              Banka Detayı
            </button>
          </div>
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                <td className="py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>Nakit</td>
                <td className="py-2 text-sm font-semibold text-right" style={{ color: 'var(--text-primary)' }}>{fmtMoney(cashAmount)}</td>
              </tr>
              <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                <td className="py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>Banka (Kart + Havale)</td>
                <td className="py-2 text-sm font-semibold text-right" style={{ color: 'var(--text-primary)' }}>{fmtMoney(bankTotal)}</td>
              </tr>
              <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                <td className="py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>Puan ile Tahsilat</td>
                <td className="py-2 text-sm font-semibold text-right" style={{ color: 'var(--text-primary)' }}>{fmtMoney(0)}</td>
              </tr>
              <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                <td className="py-2 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Toplam Tahsilat</td>
                <td className="py-2 text-sm font-bold text-right" style={{ color: '#10B981' }}>{fmtMoney(totalSales)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── EXPENSES + ANALYSIS ROW ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Expenses */}
        <div className="spa-card p-4">
          <h3 className="text-sm font-semibold font-display mb-3" style={{ color: 'var(--text-primary)' }}>Giderler (Harcamalar)</h3>
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                <td className="py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>Masraf (Malzeme + Reklam + Fatura + Bakım)</td>
                <td className="py-2 text-sm font-semibold text-right" style={{ color: 'var(--text-primary)' }}>{fmtMoney(masraf)}</td>
              </tr>
              <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                <td className="py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>Personel</td>
                <td className="py-2 text-sm font-semibold text-right" style={{ color: 'var(--text-primary)' }}>{fmtMoney(personelGider)}</td>
              </tr>
              <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                <td className="py-2 text-xs" style={{ color: 'var(--text-secondary)' }}>Kira</td>
                <td className="py-2 text-sm font-semibold text-right" style={{ color: 'var(--text-primary)' }}>{fmtMoney(kiraGider)}</td>
              </tr>
              <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                <td className="py-2 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Gider Toplamı</td>
                <td className="py-2 text-sm font-bold text-right" style={{ color: '#EF4444' }}>{fmtMoney(totalExpenses)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Analysis */}
        <div className="spa-card p-4">
          <h3 className="text-sm font-semibold font-display mb-3" style={{ color: 'var(--text-primary)' }}>Analiz</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { label: 'Gelen Müşteri', value: gelenMusteri, icon: 'ri-user-follow-line', color: '#10B981' },
              { label: 'Kaydedilen Müşteri', value: kaydedilenMusteri, icon: 'ri-user-add-line', color: '#3B82F6' },
              { label: 'Adisyon Sayısı', value: adisyonSayisi, icon: 'ri-file-list-line', color: '#C9A962' },
              { label: 'Adisyon Ort.', value: `₺${adisyonOrtalama.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`, icon: 'ri-calculator-line', color: '#8B5CF6' },
              { label: 'Net Kalan', value: fmtMoney(totalSales - totalExpenses), icon: 'ri-safe-2-line', color: '#2D5F3E' },
              { label: 'Çağrı Sayısı', value: cagriSayisi, icon: 'ri-phone-line', color: '#F59E0B' },
            ].map(item => (
              <div key={item.label} className="p-3 rounded-spa-sm flex flex-col items-center text-center" style={{ background: 'var(--bg-tertiary)' }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center mb-1.5" style={{ background: item.color + '15' }}>
                  <div className="w-3.5 h-3.5 flex items-center justify-center"><i className={`${item.icon} text-xs`} style={{ color: item.color }} /></div>
                </div>
                <p className="text-sm font-bold font-display" style={{ color: 'var(--text-primary)' }}>{item.value}</p>
                <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── STAFF DETAIL ── */}
      <div className="spa-card p-4 overflow-hidden">
        <h3 className="text-sm font-semibold font-display mb-3" style={{ color: 'var(--text-primary)' }}>Personel Detayı</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                {['Personel', 'Hizmet', 'Prim', 'Maaş', 'Avans', 'Bakiye', 'Tip'].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staffSummary.map(s => (
                <tr key={s.id} className="border-b hover:bg-theme-hover transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                  <td className="px-3 py-2.5 whitespace-nowrap font-medium" style={{ color: 'var(--text-primary)' }}>{s.name}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{s.hizmet} hizmet</td>
                  <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: '#C9A962' }}>{fmtMoney(s.prim)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{fmtMoney(s.maas)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: '#EF4444' }}>{s.avans > 0 ? `-${fmtMoney(s.avans)}` : '-'}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap font-semibold" style={{ color: s.bakiye >= 0 ? '#10B981' : '#EF4444' }}>{fmtMoney(s.bakiye)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className="spa-badge text-[10px]" style={{ background: s.compensationType === 'maaş' ? 'rgba(59,130,246,0.12)' : s.compensationType === 'prim' ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.12)', color: s.compensationType === 'maaş' ? '#3B82F6' : s.compensationType === 'prim' ? '#F59E0B' : '#10B981' }}>
                      {s.compensationType === 'maaş' ? 'Maaş' : s.compensationType === 'prim' ? 'Prim' : 'Maaş+Prim'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── VENDOR BALANCES ── */}
      <div className="spa-card p-4 overflow-hidden">
        <h3 className="text-sm font-semibold font-display mb-3" style={{ color: 'var(--text-primary)' }}>Firma Borç / Alacak</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                {['Firma', 'Toplam Fatura', 'Toplam Ödeme', 'Bakiye', 'Durum'].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vendorData.map(v => {
                const bal = v.totalBilled - v.totalPaid;
                return (
                  <tr key={v.vendor} className="border-b hover:bg-theme-hover transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                    <td className="px-3 py-2.5 whitespace-nowrap font-medium" style={{ color: 'var(--text-primary)' }}>{v.vendor}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>{fmtMoney(v.totalBilled)}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{fmtMoney(v.totalPaid)}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap font-semibold" style={{ color: bal > 0 ? '#EF4444' : '#10B981' }}>{fmtMoney(bal)}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {bal > 0 ? (
                        <span className="spa-badge text-[10px]" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>Borçlu</span>
                      ) : bal < 0 ? (
                        <span className="spa-badge text-[10px]" style={{ background: 'rgba(245,158,11,0.12)', color: '#F59E0B' }}>Alacaklı</span>
                      ) : (
                        <span className="spa-badge text-[10px]" style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }}>Kapanmış</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── BANK DETAIL MODAL ── */}
      {bankModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={() => setBankModalOpen(false)}>
          <div className="spa-card w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
              <h2 className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>Banka Detayı</h2>
              <button onClick={() => setBankModalOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer" style={{ background: 'var(--bg-tertiary)' }}>
                <i className="ri-close-line" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {bankBreakdown.length === 0 ? (
                <p className="text-sm text-center py-4" style={{ color: 'var(--text-muted)' }}>Bu dönemde banka kaydı bulunamadı.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: 'var(--bg-tertiary)' }}>
                      <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Banka</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bankBreakdown.map(b => (
                      <tr key={b.name} className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                        <td className="px-3 py-2.5" style={{ color: 'var(--text-primary)' }}>{b.name}</td>
                        <td className="px-3 py-2.5 text-right font-semibold" style={{ color: 'var(--text-primary)' }}>{fmtMoney(b.value)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="px-3 py-2.5 font-medium" style={{ color: 'var(--text-primary)' }}>Toplam</td>
                      <td className="px-3 py-2.5 text-right font-bold" style={{ color: '#C9A962' }}>{fmtMoney(bankTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}